import type { FunctionComponent } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Stack, Text } from '@atlaskit/primitives';
import Image from '@atlaskit/image';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';

import ErrorImage from '#assets/error.svg';
import { NoSitesError } from '#definitions/errors';
import { imageContainerStyling, RAISE_SUPPORT_TICKET_LINK } from './ErrorPage';

const errorMessages = defineMessages({
  noSitesHeading: {
    id: 'error.noSites.heading',
    defaultMessage: 'Access denied!',
    description:
      'Error message heading denoting that access to the site has been denied',
  },
  noSitesDescription: {
    id: 'error.noSites.description',
    defaultMessage:
      "This app requires access to a {product} site which you don't have or don't have the permission to access",
    description:
      "The app needs access to a (Confluence/Jira) site but the user doesn't have access to any",
  },
  noSitesFooter1: {
    id: 'error.noSites.footer1',
    defaultMessage: 'Visit {link} to create a site.',
    description:
      'Visit the link to create a new (Confluence/Jira) site so that the app can be installed onto a confluence site',
  },
  noSitesFooter2: {
    id: 'error.noSites.footer2',
    defaultMessage:
      "Something's not right? <link>Raise a support request</link>",
    description:
      "The user should raise a support request if they did not understand why they're being shown this error",
  },
});

const NoSitesErrorPage: FunctionComponent<NoSitesError> = ({ product }) => (
  <Stack alignInline={'center'} space={'space.100'}>
    <Image style={imageContainerStyling} src={ErrorImage} alt="error image" />
    <Heading size={'large'}>
      <FormattedMessage {...errorMessages.noSitesHeading} />
    </Heading>
    <Text align={'center'}>
      <FormattedMessage
        {...errorMessages.noSitesDescription}
        values={{ product }}
      />
    </Text>
    <Text align={'center'}>
      <FormattedMessage
        {...errorMessages.noSitesFooter1}
        values={{
          link: (
            <Link href={'https://www.atlassian.com'}>
              https://www.atlassian.com
            </Link>
          ),
        }}
      />
    </Text>
    <Text align={'center'}>
      <FormattedMessage
        {...errorMessages.noSitesFooter2}
        values={{
          link: (text) => <Link href={RAISE_SUPPORT_TICKET_LINK}>{text}</Link>,
        }}
      />
    </Text>
  </Stack>
);

export default NoSitesErrorPage;
